import { apiRequest } from "../client";
import type { ApiResponse } from "../types";
import type {
  TimeseriesRequest,
  TimeseriesResponse,
  RollingStatsRequest,
  RollingStatsResponse,
  MetricTimeseriesRequest,
  MetricTimeseriesResponse,
  CorrelationMatrixRequest,
  CorrelationMatrixResponse,
  HoldingsRequest,
  HoldingsResponse,
  HoldingsComparisonRequest,
  HoldingsComparisonResponse,
  CategoricalChartRequest,
  CategoricalChartResponse,
  ContributionsRequest,
  ContributionsResponse,
  ContributionsComparisonRequest,
  ContributionsComparisonResponse,
  ExposureRequest,
  ExposureResponse,
  ExposureTimeseriesRequest,
  ExposureTimeseriesResponse,
  ExposuresComparisonRequest,
  ExposuresComparisonResponse,
  ScatterChartRequest,
  ScatterChartResponse,
  LookthroughRequest,
  LookthroughResponse,
  CreateWidgetConfigRequest,
  WidgetConfig,
  TWRRequest,
  TWRResponse,
  IRRRequest,
  IRRResponse,
} from "./types";

/**
 * Get timeseries data for specified resources
 * @param request The timeseries request parameters
 * @returns Promise with timeseries data
 */
export async function getTimeseriesData(
  request: TimeseriesRequest
): Promise<any> {
  const response = await apiRequest<any>({
    method: "POST",
    url: "/widgets/performance-chart",
    data: request,
  });

  // The backend may respond in one of two shapes:
  // 1. Wrapped: { status: 'success', data: { ...payload } }
  // 2. Unwrapped: { data: { rows, cols, ... }, metadata: { ... } }

  // Shape-1 (wrapped) – "status" field is present
  if (Object.prototype.hasOwnProperty.call(response, "status")) {
    const wrapped = response as ApiResponse<any>;

    if (wrapped.status === "error" || !wrapped.data) {
      throw new Error(
        wrapped.error?.message || "Failed to fetch timeseries data"
      );
    }

    // Return the FULL wrapper so consumers like Playground that expect `.data.cols` still work
    return wrapped;
  }

  // Shape-2 (unwrapped) – assume the response is already the payload we need
  return response as TimeseriesResponse;
}

/**
 * Get rolling statistics data for specified resources
 * @param request The rolling stats request parameters
 * @returns Promise with rolling stats data
 */
export async function getRollingStats(
  request: RollingStatsRequest
): Promise<RollingStatsResponse> {
  const response = await apiRequest<ApiResponse<RollingStatsResponse>>({
    method: "POST",
    url: "/widgets/rolling-stats",
    data: request,
  });

  if (response.status === "error" || !response.data) {
    throw new Error(
      response.error?.message || "Failed to fetch rolling stats data"
    );
  }

  return response.data;
}

/**
 * Get precomputed metric timeseries data for specified resources
 */
export async function getMetricTimeseries(
  request: MetricTimeseriesRequest
): Promise<MetricTimeseriesResponse> {
  const response = await apiRequest<ApiResponse<MetricTimeseriesResponse>>({
    method: "POST",
    url: "/widgets/metric-timeseries",
    data: request,
  });

  if (response.status === "error" || !response.data) {
    throw new Error(
      response.error?.message || "Failed to fetch metric timeseries data"
    );
  }

  return response.data;
}

/**
 * Get correlation matrix data for specified resources
 * @param request The correlation matrix request parameters
 * @returns Promise with correlation matrix data
 */
export async function getCorrelationMatrix(
  request: CorrelationMatrixRequest
): Promise<CorrelationMatrixResponse> {
  const response = await apiRequest<CorrelationMatrixResponse | ApiResponse<CorrelationMatrixResponse>>({
    method: "POST",
    url: "/widgets/correlation-matrix",
    data: request,
  });

  // Handle both wrapped and unwrapped response formats
  if ("status" in response && response.status === "error") {
    throw new Error(
      (response as ApiResponse<CorrelationMatrixResponse>).error?.message || "Failed to fetch correlation matrix data"
    );
  }

  // If wrapped in ApiResponse, extract data
  if ("data" in response && response.data && "matrix" in response.data) {
    return response.data as CorrelationMatrixResponse;
  }

  // Otherwise return as-is (direct response)
  return response as CorrelationMatrixResponse;
}

/**
 * Get holdings data for specified resources
 * @param request The holdings request parameters
 * @returns Promise with holdings data
 */
export async function getHoldings(
  request: HoldingsRequest
): Promise<HoldingsResponse> {
  const response = await apiRequest<
    ApiResponse<HoldingsResponse> | HoldingsResponse
  >({
    method: "POST",
    url: "/widgets/holdings",
    data: request,
  });

  if ("status" in response) {
    if (response.status === "error" || !response.data) {
      throw new Error(response.error?.message || "Failed to fetch holdings data");
    }
    return response.data;
  }

  if ("metadata" in response && "data" in response) {
    return response;
  }

  throw new Error("Unexpected holdings response shape");
}

/**
 * Get full holdings data for specified resources
 * @param request The holdings request parameters
 * @returns Promise with full holdings response including metadata
 */
export async function getFullHoldings(request: HoldingsRequest): Promise<any> {
  const response = await apiRequest<ApiResponse<any>>({
    method: "POST",
    url: "/widgets/holdings",
    data: request,
  });

  if (response.status === "error" || !response.data) {
    throw new Error(response.error?.message || "Failed to fetch holdings data");
  }

  // Return the FULL response which contains the complete API response
  // response is the actual API response with both data and metadata
  return response;
}

/**
 * Get holdings comparison data for multiple portfolios
 * Returns a merged view with side-by-side columns for each portfolio's holdings.
 * Response format matches regular holdings API for consistency.
 *
 * @param request The holdings comparison request parameters
 * @returns Promise with merged holdings data in HoldingsResponse format
 */
export async function getHoldingsComparison(
  request: HoldingsComparisonRequest
): Promise<HoldingsResponse> {
  const response = await apiRequest<any>({
    method: "POST",
    url: "/widgets/holdings-comparison",
    data: request,
  });

  // Handle wrapped response shape: { status: 'success', data: {...} }
  if (Object.prototype.hasOwnProperty.call(response, "status")) {
    const wrapped = response as ApiResponse<HoldingsResponse>;
    if (wrapped.status === "error" || !wrapped.data) {
      throw new Error(
        wrapped.error?.message || "Failed to fetch holdings comparison data"
      );
    }
    return wrapped.data;
  }

  // Unwrapped response - return directly
  return response as HoldingsResponse;
}

/**
 * Get contributions data for a portfolio
 * @param request The contributions request parameters
 * @returns Promise with contributions data
 */
export async function getContributions(
  request: ContributionsRequest
): Promise<ContributionsResponse> {
  const response = await apiRequest<ApiResponse<ContributionsResponse>>({
    method: "POST",
    url: "/widgets/contributions/table",
    data: request,
  });

  if (response.status === "error" || !response.data) {
    throw new Error(
      response.error?.message || "Failed to fetch contributions data"
    );
  }

  return response.data;
}

export interface ContributionsFullMetadata {
  portfolio_id?: string;
  portfolio_currency?: string;
  product_id_map?: Record<string, unknown>;
  holdings_only?: boolean;
  contributions_data_from?: string | null;
  contributions_data_through?: string | null;
  start_boundary_date?: string;
  end_boundary_date?: string;
  enable_lookthrough?: boolean;
  [key: string]: unknown;
}

export interface ContributionsFullResponse {
  data: {
    table: {
      cols: Array<{ id: string; label: string; type: string }>;
      rows: Array<Array<string | number>>;
      statistics?: unknown;
    };
  };
  metadata: ContributionsFullMetadata;
}

/** Normalize wrapped or unwrapped contributions table API payloads. */
export function normalizeContributionsFullResponse(
  response: unknown
): ContributionsFullResponse {
  const root = response as Record<string, unknown>;
  if (!root) {
    throw new Error("Invalid API response format");
  }

  if (root.status === "error") {
    const error = root.error as { message?: string } | undefined;
    throw new Error(error?.message || "Failed to fetch contributions data");
  }

  const rootMetadata = root.metadata as ContributionsFullMetadata | undefined;

  const payload =
    root.status != null && root.data != null
      ? (root.data as Record<string, unknown>)
      : root;

  const resolveMetadata = (
    payloadMetadata: unknown
  ): ContributionsFullMetadata =>
    (payloadMetadata as ContributionsFullMetadata) ??
    rootMetadata ??
    {};

  if (payload.table) {
    return {
      data: { table: payload.table as ContributionsFullResponse["data"]["table"] },
      metadata: resolveMetadata(payload.metadata),
    };
  }

  const nestedData = payload.data as Record<string, unknown> | undefined;
  if (nestedData?.table) {
    return {
      data: { table: nestedData.table as ContributionsFullResponse["data"]["table"] },
      metadata: resolveMetadata(payload.metadata),
    };
  }

  // Legacy shape: data keyed by portfolio id -> { cols, rows }
  const legacyTable = findLegacyContributionsTable(payload.data ?? payload);
  if (legacyTable) {
    return {
      data: { table: legacyTable },
      metadata: resolveMetadata(payload.metadata),
    };
  }

  throw new Error("Invalid API response format");
}

function findLegacyContributionsTable(
  data: unknown
): ContributionsFullResponse["data"]["table"] | null {
  if (!data || typeof data !== "object") {
    return null;
  }

  const record = data as Record<string, unknown>;
  if (Array.isArray(record.cols) && Array.isArray(record.rows)) {
    return record as ContributionsFullResponse["data"]["table"];
  }

  for (const value of Object.values(record)) {
    if (
      value &&
      typeof value === "object" &&
      Array.isArray((value as { cols?: unknown }).cols) &&
      Array.isArray((value as { rows?: unknown }).rows)
    ) {
      return value as ContributionsFullResponse["data"]["table"];
    }
  }

  return null;
}

/**
 * Get full contributions data for a portfolio (includes metadata)
 * @param request The contributions request parameters
 * @returns Promise with full contributions response including metadata
 */
export async function getFullContributions(
  request: ContributionsRequest
): Promise<ContributionsFullResponse> {
  const response = await apiRequest<ApiResponse<any>>({
    method: "POST",
    url: "/widgets/contributions/table",
    data: request,
  });

  return normalizeContributionsFullResponse(response);
}

/**
 * Get contributions comparison data for multiple portfolios.
 * Returns a merged view with side-by-side columns for each portfolio's contributions.
 *
 * @param request The contributions comparison request parameters
 * @returns Promise with merged contributions data
 */
export async function getContributionsComparison(
  request: ContributionsComparisonRequest
): Promise<ContributionsComparisonResponse> {
  const response = await apiRequest<any>({
    method: "POST",
    url: "/widgets/contributions-comparison",
    data: request,
  });

  return normalizeContributionsComparisonResponse(response);
}

/** Normalize wrapped or unwrapped contributions comparison API payloads. */
export function normalizeContributionsComparisonResponse(
  response: unknown
): ContributionsComparisonResponse {
  const root = response as Record<string, unknown>;
  if (!root) {
    throw new Error("Invalid API response format");
  }

  if (root.status === "error") {
    const error = root.error as { message?: string } | undefined;
    throw new Error(
      error?.message || "Failed to fetch contributions comparison data"
    );
  }

  const rootMetadata = root.metadata as
    | ContributionsComparisonResponse["metadata"]
    | undefined;

  const payload =
    root.status != null && root.data != null
      ? (root.data as Record<string, unknown>)
      : root;

  const resolveMetadata = (): ContributionsComparisonResponse["metadata"] =>
    (payload.metadata as ContributionsComparisonResponse["metadata"]) ??
    rootMetadata ??
    ({} as ContributionsComparisonResponse["metadata"]);

  const nestedData = payload.data as Record<string, unknown> | undefined;
  if (nestedData?.comparison) {
    return {
      data: {
        comparison:
          nestedData.comparison as ContributionsComparisonResponse["data"]["comparison"],
      },
      metadata: resolveMetadata(),
    };
  }

  if (payload.comparison) {
    return {
      data: {
        comparison:
          payload.comparison as ContributionsComparisonResponse["data"]["comparison"],
      },
      metadata: resolveMetadata(),
    };
  }

  const direct = payload as unknown as ContributionsComparisonResponse;
  if (direct.data?.comparison) {
    return {
      data: direct.data,
      metadata: direct.metadata ?? resolveMetadata(),
    };
  }

  throw new Error("Invalid API response format");
}

/**
 * Get exposures comparison data for multiple portfolios.
 * Returns a merged view with side-by-side columns for each portfolio's exposure allocations.
 *
 * @param request The exposures comparison request parameters
 * @returns Promise with merged exposures data
 */
export async function getExposuresComparison(
  request: ExposuresComparisonRequest
): Promise<ExposuresComparisonResponse> {
  const response = await apiRequest<any>({
    method: "POST",
    url: "/widgets/exposures-comparison",
    data: request,
  });

  // Handle wrapped response shape: { status: 'success', data: {...} }
  if (Object.prototype.hasOwnProperty.call(response, "status")) {
    const wrapped = response as ApiResponse<ExposuresComparisonResponse>;
    if (wrapped.status === "error" || !wrapped.data) {
      throw new Error(
        wrapped.error?.message || "Failed to fetch exposures comparison data"
      );
    }
    return wrapped.data;
  }

  // Unwrapped response - return directly
  return response as ExposuresComparisonResponse;
}

/**
 * Get exposure data for specified resources
 * @param request The exposure request parameters
 * @returns Promise with exposure data
 */
export async function getExposures(
  request: ExposureRequest
): Promise<ExposureResponse> {
  const response = await apiRequest<ApiResponse<ExposureResponse>>({
    method: "POST",
    url: "/widgets/exposures",
    data: request,
  });

  if (response.status === "error" || !response.data) {
    throw new Error(response.error?.message || "Failed to fetch exposure data");
  }

  return response.data;
}

/**
 * Get exposure-over-time data for a single resource
 */
export async function getExposureTimeseries(
  request: ExposureTimeseriesRequest
): Promise<ExposureTimeseriesResponse> {
  const response = await apiRequest<
    ExposureTimeseriesResponse | ApiResponse<ExposureTimeseriesResponse>
  >({
    method: "POST",
    url: "/widgets/exposure-timeseries",
    data: request,
  });

  if ("status" in response && response.status === "error") {
    throw new Error(
      response.error?.message || "Failed to fetch exposure timeseries data"
    );
  }

  if ("status" in response && response.data) {
    return response.data;
  }

  if ("data" in response && "metadata" in response) {
    return response as ExposureTimeseriesResponse;
  }

  throw new Error("Failed to fetch exposure timeseries data");
}

/**
 * Get scatter chart data comparing specified resources
 * @param request The scatter chart request parameters
 * @returns Promise with scatter chart data
 */
export async function getScatterChart(
  request: ScatterChartRequest
): Promise<ScatterChartResponse> {
  const response = await apiRequest<ApiResponse<ScatterChartResponse>>({
    method: "POST",
    url: "/widgets/scatter-chart",
    data: request,
  });

  if (response.status === "error" || !response.data) {
    throw new Error(
      response.error?.message || "Failed to fetch scatter chart data"
    );
  }

  return response.data;
}

/**
 * Get categorical chart data for specified resources and fields
 * @param request The categorical chart request parameters
 * @returns Promise with categorical data in rows/cols form per resource
 */
export async function getCategoricalChart(
  request: CategoricalChartRequest
): Promise<CategoricalChartResponse> {
  const response = await apiRequest<any>({
    method: "POST",
    url: "/widgets/categorical-chart",
    data: request,
  });

  // Some backends may not wrap data; normalize to our expected type
  if (response?.status === "success" && response?.data) {
    return response.data as CategoricalChartResponse;
  }
  return response as CategoricalChartResponse;
}

/**
 * Create a new widget configuration
 * @param request The widget configuration request
 * @returns Promise with the created widget configuration
 */
export async function createWidgetConfig(
  request: CreateWidgetConfigRequest
): Promise<WidgetConfig> {
  const response = await apiRequest<ApiResponse<WidgetConfig>>({
    method: "POST",
    url: "/widgets/configs/",
    data: request,
  });

  if (response.status === "error" || !response) {
    throw new Error(
      response.error?.message || "Failed to create widget configuration"
    );
  }

  return (
    (response as ApiResponse<WidgetConfig>).data ??
    (response as unknown as WidgetConfig)
  );
}

/**
 * Update a widget configuration
 * @param request The widget configuration request
 * @returns Promise with the updated widget configuration
 */
export async function updateWidgetConfig(
  configId: string,
  request: CreateWidgetConfigRequest
): Promise<WidgetConfig> {
  const response = await apiRequest<ApiResponse<WidgetConfig>>({
    method: "PUT",
    url: `/widgets/configs/${configId}/`,
    data: request,
  });

  if (response.status === "error" || !response) {
    throw new Error(
      response.error?.message || "Failed to update widget configuration"
    );
  }

  return (
    (response as ApiResponse<WidgetConfig>).data ??
    (response as unknown as WidgetConfig)
  );
}

/**
 * Get a specific widget configuration by ID
 * @param configId The widget configuration ID
 * @returns Promise with the widget configuration
 */
export async function getWidgetConfig(configId: string): Promise<WidgetConfig> {
  const response = await apiRequest<ApiResponse<WidgetConfig>>({
    method: "GET",
    url: `/widgets/configs/${configId}/`,
  });

  if (response.status === "error" || !response) {
    throw new Error(
      response.error?.message || "Failed to fetch widget configuration"
    );
  }

  return (
    (response as ApiResponse<WidgetConfig>).data ??
    (response as unknown as WidgetConfig)
  );
}

/**
 * Get all widget configurations
 * @returns Promise with array of widget configurations
 */
export async function getWidgetConfigs(): Promise<WidgetConfig[]> {
  const response = await apiRequest<ApiResponse<WidgetConfig[]>>({
    method: "GET",
    url: "/widgets/configs",
  });

  if (response.status === "error" || !response) {
    throw new Error(
      response.error?.message || "Failed to fetch widget configurations"
    );
  }

  return (
    (response as ApiResponse<WidgetConfig[]>).data ??
    (response as unknown as WidgetConfig[])
  );
}

/**
 * Get available exposure types for a resource
 * @param resource The resource to get available exposures for
 * @returns Promise with available exposure types
 */
export async function getAvailableExposures(resource: {
  resource_type: string;
  resource_id: string;
}): Promise<{
  exposure_types: string[];
  metadata: { as_at: string; resource_type: string; resource_id: string };
}> {
  const response = await apiRequest<
    ApiResponse<{
      exposure_types: string[];
      metadata: { as_at: string; resource_type: string; resource_id: string };
    }>
  >({
    method: "POST",
    url: "/widgets/available-exposures",
    data: { resource },
  });

  console.log("RESPONSE", response);

  if (response.status === "error") {
    throw new Error(
      response.error?.message || "Failed to fetch available exposures"
    );
  }

  return (
    (
      response as ApiResponse<{
        exposure_types: string[];
        metadata: { as_at: string; resource_type: string; resource_id: string };
      }>
    ).data ??
    (response as unknown as {
      exposure_types: string[];
      metadata: { as_at: string; resource_type: string; resource_id: string };
    })
  );
}

/**
 * Get lookthrough/exposure breakdown data for specified resources
 * @param request The lookthrough request parameters
 * @returns Promise with lookthrough data
 */
export async function getExposureBreakdown(
  request: LookthroughRequest
): Promise<LookthroughResponse> {
  const response = await apiRequest<ApiResponse<LookthroughResponse>>({
    method: "POST",
    url: "/widgets/exposure-breakdown",
    data: request,
  });

  console.log("getExposureBreakdown raw response:", response);

  if (response.status === "error" || !response.data) {
    throw new Error(response.error?.message || "Failed to fetch lookthrough data");
  }

  return response.data;
}

/**
 * Get TWR (Time Weighted Return) data for a portfolio
 * @param request The TWR request parameters
 * @returns Promise with TWR data
 */
export async function getTWRData(request: TWRRequest): Promise<any> {
  const response = await apiRequest<ApiResponse<any>>({
    method: "POST",
    url: "/widgets/twr/table",
    data: request,
  });

  if (response.status === "error" || !response.data) {
    throw new Error(response.error?.message || "Failed to fetch TWR data");
  }

  // Return the full response to include metadata with product_id_map
  return response;
}

/**
 * Get IRR (Internal Rate of Return) data for a portfolio
 * @param request The IRR request parameters
 * @returns Promise with IRR data
 */
export async function getIRRData(request: IRRRequest): Promise<any> {
  const response = await apiRequest<ApiResponse<any>>({
    method: "POST",
    url: "/widgets/irr",
    data: request,
  });

  if (response.status === "error" || !response.data) {
    throw new Error(response.error?.message || "Failed to fetch IRR data");
  }

  // Return the full response to include metadata with holding_id_map
  return response;
}

// Re-export types
export * from "./types";
